import React, { useState, useEffect } from 'react'
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Upload,
  message,
  DatePicker,
  Layout,
  Typography,
  Space,
  Steps,
  Image,
  Modal,
  InputNumber,
  QRCode,
  Table,
  Tag,
  Popconfirm,
  Tooltip,
  Dropdown,
  Spin,
  Skeleton
} from 'antd'
import styled from 'styled-components'
import {
  UploadOutlined,
  ClockCircleOutlined,
  CustomerServiceOutlined,
  ArrowRightOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
  MobileOutlined,
  BankOutlined,
  IdcardOutlined,
  QrcodeOutlined,
  CopyOutlined,
  StopOutlined,
  RollbackOutlined,
  SearchOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons'
import { useAuth } from '../hooks/AuthContext'
import api, { getApiErrorMessage } from '../utils/api'
import dayjs from 'dayjs'
import bankPost from '../utils/bankPost'
import cityData from '../utils/cityData'
import { useNavigate } from 'react-router-dom'
import Hint from './common/Hint'

const { Option } = Select
const { TextArea } = Input
const { Title, Text, Paragraph } = Typography

const StyledLayout = styled(Layout)`
  min-height: 100vh;
  border-radius: 16px;
  background: linear-gradient(180deg, #f4f6f9 0%, #eef1f6 48%, #f8f9fb 100%);
  padding: 8px 4px 32px;

  @media (max-width: 768px) {
    padding: 4px 0 24px;
  }
`

const StyledCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06), 0 12px 32px rgba(15, 23, 42, 0.04);
  background: #fff;
`

const HeroSection = styled.div`
  text-align: center;
  padding: 44px 28px 40px;
  background: linear-gradient(
    135deg,
    #0f172a 0%,
    #1e293b 42%,
    #1d4ed8 120%
  );
  border-radius: 20px;
  color: white;
  margin-bottom: 28px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.18);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse 80% 60% at 80% 0%,
      rgba(96, 165, 250, 0.25),
      transparent 55%
    );
    pointer-events: none;
  }

  .ant-typography {
    position: relative;
    z-index: 1;
  }
`

const FeatureCard = styled(Card)`
  height: 100%;
  border-radius: 16px;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.2s;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  margin-bottom: 16px;
  background: #fff;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
    border-color: rgba(37, 99, 235, 0.2);
  }

  .ant-card-body {
    padding: 22px 20px;
  }

  @media (max-width: 768px) {
    margin-bottom: 16px;

    .ant-card-body {
      padding: 18px 16px;
    }
  }
`

const FeatureIcon = styled.div`
  font-size: 40px;
  color: #2563eb;
  margin-bottom: 14px;
  text-align: center;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 34px;
    margin-bottom: 10px;
  }
`

const FeatureTitle = styled(Title)`
  text-align: center;
  margin-bottom: 8px !important;
  font-weight: 600 !important;

  @media (max-width: 768px) {
    font-size: 17px !important;
  }
`

const FeatureDescription = styled(Paragraph)`
  text-align: center;
  margin-bottom: 0 !important;
  color: rgba(0, 0, 0, 0.55);
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`

const PendingPanel = styled.div`
  display: flex;
  gap: 18px;
  align-items: flex-start;
  padding: 20px 22px 20px 18px;
  margin-bottom: 24px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(245, 158, 11, 0.22);
  border-left: 4px solid #f59e0b;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
`

const PendingIconWrap = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
`

const FormSection = styled.div`
  margin-bottom: 28px;
  padding: 22px 22px 8px;
  background: #f8fafc;
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.05);
`

const FormSectionTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  font-size: 17px;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.01em;

  .anticon {
    margin-right: 12px;
    font-size: 22px;
    color: #2563eb;
  }
`

const PageShell = styled.div`
  max-width: 720px;
  margin: 0 auto;
`

const PageHeaderBlock = styled.div`
  margin-bottom: 20px;
  padding: 4px 2px 0;

  .ant-typography {
    margin-bottom: 4px !important;
  }
`

const QuickPaymentCard = styled(Card)`
  margin-top: 0;
  border-radius: 20px;
  border: 1px solid rgba(15, 23, 42, 0.07);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06), 0 20px 48px rgba(15, 23, 42, 0.06);
  background: #fff;

  .ant-card-body {
    padding: 24px 22px 28px;
  }

  @media (max-width: 768px) {
    .ant-card-body {
      padding: 20px 16px 24px;
    }
  }
`

const NumberInputWrapper = styled.div`
  .ant-input-number {
    width: 100%;
    height: 48px;
    font-size: 24px;
    text-align: center;

    .ant-input-number-input {
      height: 48px;
      font-size: 24px;
      text-align: center;
    }
  }

  @media (max-width: 768px) {
    .ant-input-number {
      height: 56px;

      .ant-input-number-input {
        height: 56px;
        font-size: 28px;
      }
    }
  }
`

const QuickAmountButton = styled(Button)`
  height: 48px;
  font-size: 18px;
  margin: 4px;

  @media (max-width: 768px) {
    height: 56px;
    font-size: 20px;
  }
`

const QuickAmountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 16px;
`

const QRCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 22px 18px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  margin-top: 20px;
  border: 1px solid rgba(15, 23, 42, 0.06);
`

const PaymentLinkContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 12px 14px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  width: 100%;
  max-width: 100%;
`

const NumberKeypad = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 16px;
  padding: 18px;
  background: #f1f5f9;
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.05);
`

const NumberKey = styled(Button)`
  height: 56px;
  font-size: 22px;
  font-weight: 600;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  &:hover {
    background: #f8fafc !important;
    border-color: rgba(37, 99, 235, 0.35) !important;
    color: #1d4ed8 !important;
  }

  &:active {
    background: #eff6ff !important;
  }
`

const NumberDisplay = styled.div`
  background: #fff;
  padding: 18px 20px;
  border-radius: 14px;
  margin-bottom: 4px;
  text-align: right;
  font-size: clamp(26px, 5vw, 34px);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  border: 1px solid rgba(15, 23, 42, 0.08);
  min-height: 68px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  color: ${(p) => (p.$empty ? 'rgba(15, 23, 42, 0.35)' : '#0f172a')};
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
`

const SettingsLoadingShell = styled.div`
  min-height: 420px;
  padding: 32px 24px 48px;
  border-radius: 12px;
  background: linear-gradient(165deg, #f0f7ff 0%, #fafafa 45%, #ffffff 100%);
`

const SettingsLoadingHero = styled.div`
  height: 132px;
  border-radius: 12px;
  margin-bottom: 28px;
  background: linear-gradient(
    90deg,
    rgba(24, 144, 255, 0.12) 0%,
    rgba(24, 144, 255, 0.06) 50%,
    rgba(24, 144, 255, 0.1) 100%
  );
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.5) 45%,
      transparent 90%
    );
    animation: paySettingsShimmer 1.35s ease-in-out infinite;
  }

  @keyframes paySettingsShimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`

const SettingsSpinWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 8px 0 24px;

  .ant-spin-text {
    color: rgba(0, 0, 0, 0.55);
    font-size: 14px;
    letter-spacing: 0.02em;
  }
`

const ContentReveal = styled.div`
  animation: paySettingsContentIn 0.4s cubic-bezier(0.22, 1, 0.36, 1);
  @keyframes paySettingsContentIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const PaymentSettings = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [submitApplicationLoading, setSubmitApplicationLoading] =
    useState(false)
  const [quickPaymentLoading, setQuickPaymentLoading] = useState(false)
  const [hasApplied, setHasApplied] = useState(false)
  const [applicationStatus, setApplicationStatus] = useState(null)
  const [showHelpModal, setShowHelpModal] = useState(false)
  const [helpMessage, setHelpMessage] = useState('')
  const { user } = useAuth()
  const businessId = user.businessId
  const [selectedBankCategory, setSelectedBankCategory] = useState(null)
  const [selectedCity, setSelectedCity] = useState(null)
  const [districtOptions, setDistrictOptions] = useState([])
  const [paymentAmount, setPaymentAmount] = useState(0)
  const [paymentLink, setPaymentLink] = useState('')
  const [showQRCode, setShowQRCode] = useState(false)
  const [linkExpiry, setLinkExpiry] = useState(24)
  const [paymentRecords, setPaymentRecords] = useState([])
  const [showPaymentRecords, setShowPaymentRecords] = useState(false)

  useEffect(() => {
    if (!businessId) {
      setSettingsLoading(false)
      return
    }
    fetchPaymentSettings()
  }, [businessId])

  const fetchPaymentSettings = async () => {
    if (!businessId) return
    setSettingsLoading(true)
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/payment-settings`
      )
      const { hasApplied, applicationStatus } = response.data.data
      setHasApplied(hasApplied)
      setApplicationStatus(applicationStatus)
    } catch (error) {
      console.error('獲取支付設置失敗：', error)
      message.error(getApiErrorMessage(error, '獲取支付設置失敗'))
    } finally {
      setSettingsLoading(false)
    }
  }

  const handleSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        setSubmitApplicationLoading(true)
        try {
          const formData = new FormData()
          Object.keys(values).forEach((key) => {
            if (
              key === 'idCardFront' ||
              key === 'idCardBack' ||
              key === 'healthCardImage' ||
              key === 'bankbookImage'
            ) {
              if (values[key]?.[0]?.originFileObj) {
                formData.append(key, values[key][0].originFileObj)
              }
            } else if (key === 'idCardIssueDate') {
              formData.append(key, values[key].format('YYYY-MM-DD'))
            } else {
              formData.append(key, values[key])
            }
          })

          await api.post(
            `/api/business_B/${businessId}/payment-application`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          )

          message.success('申請已提交，我們會盡快審核')
          setHasApplied(true)
          setApplicationStatus('pending')
          form.resetFields()
        } catch (error) {
          console.error(error)
          const msg = getApiErrorMessage(error, '提交失敗，請稍後重試')
          message.error(msg)
        } finally {
          setSubmitApplicationLoading(false)
        }
      })
      .catch((errorInfo) => {
        // 驗證失敗，顯示錯誤提示
        if (errorInfo?.errorFields && errorInfo.errorFields.length > 0) {
          const errorMessages = errorInfo.errorFields
            .map((field) => field.errors?.[0])
            .filter(Boolean)
          
          if (errorMessages.length > 0) {
            // 顯示第一個錯誤訊息
            message.error(errorMessages[0])
            
            // 如果有多個錯誤，顯示總數提示
            if (errorMessages.length > 1) {
              setTimeout(() => {
                message.warning(`還有 ${errorMessages.length - 1} 個項目需要填寫`)
              }, 500)
            }
            
            // 滾動到第一個錯誤字段
            const firstErrorField = errorInfo.errorFields[0]
            if (firstErrorField?.name) {
              const fieldName = Array.isArray(firstErrorField.name)
                ? firstErrorField.name[0]
                : firstErrorField.name
              const element = document.querySelector(
                `[name="${fieldName}"]`
              )
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                // 聚焦到第一個錯誤字段
                setTimeout(() => {
                  const input = element.querySelector('input, textarea, select')
                  if (input) {
                    input.focus()
                  }
                }, 300)
              }
            }
          } else {
            message.error('請填寫所有必填項目')
          }
        } else {
          message.error('請檢查表單並填寫所有必填項目')
        }
      })
  }

  const handleGeneratePaymentLink = async () => {
    if (paymentAmount <= 0) {
      message.warning('請輸入有效的收款金額')
      return
    }

    try {
      setQuickPaymentLoading(true)
      const response = await api.post('/api/payments/quick-payment', {
        amount: paymentAmount,
        expiry_hours: linkExpiry,
        business: {
          id: businessId,
          name: user.businessName || user.name,
          username: user.username
        }
      })

      const { payment_link, order_sn, payment_data } = response.data.data
      setPaymentLink(payment_link)
      setShowQRCode(true)
      message.success('收款連結生成成功')
    } catch (error) {
      console.error('生成收款連結失敗:', error)
      message.error(getApiErrorMessage(error, '生成收款連結失敗'))
    } finally {
      setQuickPaymentLoading(false)
    }
  }

  const fetchPaymentRecords = async () => {
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/payment-records`
      )
      setPaymentRecords(response.data.records)
    } catch (error) {
      console.error('獲取支付記錄失敗:', error)
      message.error(getApiErrorMessage(error, '獲取支付記錄失敗'))
    }
  }

  const renderQuickPayment = () => {
    if (!hasApplied || applicationStatus !== 'approved') {
      return null
    }

    const handleNumberClick = (num) => {
      if (num === 'clear') {
        setPaymentAmount(0)
      } else if (num === 'backspace') {
        setPaymentAmount((prev) => Math.floor(prev / 10))
      } else {
        setPaymentAmount((prev) => {
          const newValue = prev * 10 + num
          return newValue > 999999 ? prev : newValue
        })
      }
    }

    const renderNumberKeypad = () => {
      const numbers = [
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
        { value: 6, label: '6' },
        { value: 7, label: '7' },
        { value: 8, label: '8' },
        { value: 9, label: '9' },
        { value: 'clear', label: '清除' },
        { value: 0, label: '0' },
        { value: 'backspace', label: '←' }
      ]

      return (
        <NumberKeypad>
          {numbers.map(({ value, label }) => (
            <NumberKey key={value} onClick={() => handleNumberClick(value)}>
              {label}
            </NumberKey>
          ))}
        </NumberKeypad>
      )
    }

    return (
      <Spin spinning={quickPaymentLoading} tip='正在產生收款連結…'>
        <QuickPaymentCard>
        <PageHeaderBlock>
          <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
            快速收款
          </Title>
          <Text type='secondary' style={{ fontSize: 14 }}>
            輸入金額後產生連結或 QR Code，顧客開啟即可付款
          </Text>
        </PageHeaderBlock>

        <Form layout='vertical'>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label='收款金額'
                tooltip='此收款金額將做為付款金額被收取'
              >
                <NumberDisplay $empty={paymentAmount === 0}>
                  {paymentAmount === 0
                    ? '請輸入金額'
                    : `NT$ ${paymentAmount.toLocaleString()}`}
                </NumberDisplay>
                {renderNumberKeypad()}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label='連結有效期' tooltip='設置收款連結的有效期'>
                <Select
                  value={linkExpiry}
                  onChange={setLinkExpiry}
                  style={{ width: '100%', borderRadius: 10 }}
                  popupMatchSelectWidth={false}
                >
                  <Option value={1}>1小時</Option>
                  <Option value={6}>6小時</Option>
                  <Option value={12}>12小時</Option>
                  <Option value={24}>24小時</Option>
                  <Option value={48}>48小時</Option>
                  <Option value={72}>72小時</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Button
                type='primary'
                icon={<QrcodeOutlined />}
                onClick={handleGeneratePaymentLink}
                disabled={quickPaymentLoading}
                style={{
                  width: '100%',
                  height: 50,
                  fontSize: 16,
                  fontWeight: 600,
                  borderRadius: 12,
                  marginTop: 4
                }}
              >
                產生收款連結
              </Button>
            </Col>
          </Row>
        </Form>

        {paymentLink && (
          <>
            <QRCodeContainer>
              {showQRCode && (
                <QRCode
                  value={paymentLink}
                  size={200}
                  level='H'
                  style={{ marginBottom: 16 }}
                />
              )}
              <PaymentLinkContainer>
                <Input value={paymentLink} readOnly style={{ flex: 1 }} />
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(paymentLink)
                    message.success('已複製到剪貼簿')
                  }}
                >
                  複製
                </Button>
                <Button
                  type={showQRCode ? 'default' : 'primary'}
                  onClick={() => setShowQRCode(!showQRCode)}
                >
                  {showQRCode ? '隱藏QR碼' : '顯示QR碼'}
                </Button>
              </PaymentLinkContainer>
            </QRCodeContainer>
          </>
        )}
        </QuickPaymentCard>
      </Spin>
    )
  }

  const renderStatusContent = () => {
    if (applicationStatus === 'pending') {
      return (
        <PageShell>
          <PendingPanel>
            <PendingIconWrap>
              <ClockCircleOutlined />
            </PendingIconWrap>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Title
                level={4}
                style={{ margin: '0 0 8px', fontWeight: 600, fontSize: 18 }}
              >
                金流申請審核中
              </Title>
              <Paragraph
                style={{ marginBottom: 16, color: 'rgba(0,0,0,0.55)' }}
              >
                我們會盡快完成審核，通過後即可使用線上收款與預約付款。
              </Paragraph>
              <Hint
                title='需要協助？'
                tone='info'
                style={{
                  borderRadius: 12,
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  background: 'rgba(239, 246, 255, 0.65)'
                }}
                action={
                  <Button
                    type='primary'
                    icon={<CustomerServiceOutlined />}
                    onClick={() => setShowHelpModal(true)}
                    style={{ borderRadius: 10 }}
                  >
                    聯繫客服
                  </Button>
                }
              >
                若填寫資料有疑問，可聯繫客服協助。
              </Hint>
            </div>
          </PendingPanel>
        </PageShell>
      )
    }

    return (
      <PageShell>
        {renderQuickPayment()}
      </PageShell>
    )
  }

  const renderApplicationForm = () => {
    return (
      <Spin
        spinning={submitApplicationLoading}
        tip='提交申請中，請稍候…'
        size='large'
        style={{ width: '100%', maxWidth: '100%' }}
      >
        <div style={{ width: '100%' }}>
        <HeroSection>
          <Title
            level={2}
            style={{
              color: 'white',
              marginBottom: 12,
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}
          >
            開啟線上支付
          </Title>
          <Paragraph
            style={{
              color: 'rgba(255,255,255,0.88)',
              fontSize: 16,
              maxWidth: 520,
              margin: '0 auto',
              lineHeight: 1.65
            }}
          >
            一次申請，讓顧客線上付款預約金額，收款更安全、流程更順暢
          </Paragraph>
        </HeroSection>

        <Row gutter={[24, 24]} style={{ marginBottom: 48 }}>
          <Col xs={24} sm={24} md={8}>
            <FeatureCard>
              <FeatureIcon>
                <DollarOutlined />
              </FeatureIcon>
              <FeatureTitle level={4}>提高轉換率</FeatureTitle>
              <FeatureDescription>
                提供便捷的支付方式，減少顧客流失，提高預約轉換率
              </FeatureDescription>
            </FeatureCard>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <FeatureCard>
              <FeatureIcon>
                <SafetyCertificateOutlined />
              </FeatureIcon>
              <FeatureTitle level={4}>安全可靠</FeatureTitle>
              <FeatureDescription>
                採用先進的支付安全技術，保障每筆交易的安全
              </FeatureDescription>
            </FeatureCard>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <FeatureCard>
              <FeatureIcon>
                <MobileOutlined />
              </FeatureIcon>
              <FeatureTitle level={4}>便捷管理</FeatureTitle>
              <FeatureDescription>
                隨時查看交易記錄，輕鬆管理收款，提高工作效率
              </FeatureDescription>
            </FeatureCard>
          </Col>
        </Row>

        <StyledCard>
          <Steps
            current={0}
            items={[
              {
                title: '填寫申請資料',
                description: '提供必要的商家資訊'
              },
              {
                title: '提交審核',
                description: '等待審核結果'
              },
              {
                title: '開始收款',
                description: '開通線上支付'
              }
            ]}
            style={{ marginBottom: 40 }}
          />

          <Form form={form} layout='vertical' requiredMark={false}>
            <FormSection>
              <FormSectionTitle>
                <IdcardOutlined />
                基本信息
              </FormSectionTitle>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name='storeType'
                    label='合作商店類型'
                    rules={[{ required: true, message: '請選擇合作商店類型' }]}
                  >
                    <Select>
                      <Option value='individual'>個人型合作商店</Option>
                      <Option value='company'>公司型合作商店</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name='memberName'
                    label='會員名稱'
                    rules={[{ required: true, message: '請輸入會員名稱' }]}
                  >
                    <Input placeholder='請輸入會員名稱' />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name='memberEnglishName'
                    label='會員英文名稱'
                    rules={[{ required: true, message: '請輸入會員英文名稱' }]}
                  >
                    <Input placeholder='格式為名,姓' />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name='birthDate'
                    label='出生年月日'
                    rules={[{ required: true, message: '請選擇出生年月日' }]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name='adminAccount'
                    label='管理帳號'
                    rules={[
                      { required: true, message: '請輸入管理帳號' },
                      { max: 20, message: '管理帳號不能超過20個字符' },
                      {
                        pattern: /^[a-zA-Z0-9_.@]+$/,
                        message: '只能包含英文字母、數字、_、.、@'
                      }
                    ]}
                  >
                    <Input placeholder='英數混合，最長20個字符' />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name='adminPhone'
                    label='管理者手機'
                    rules={[{ required: true, message: '請輸入管理者手機' }]}
                  >
                    <Input placeholder='請輸入手機號碼' />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name='adminEmail'
                    label='管理者信箱'
                    rules={[
                      { required: true, message: '請輸入管理者信箱' },
                      { type: 'email', message: '請輸入有效的郵箱地址' }
                    ]}
                  >
                    <Input placeholder='請輸入郵箱地址' />
                  </Form.Item>
                </Col>
              </Row>

              <p style={{ marginTop: 5 }}>會員聯絡地址</p>

              <Row gutter={8} align='middle' style={{ marginBottom: 0 }}>
                <Col>
                  <Form.Item
                    name='city'
                    rules={[{ required: true, message: '請選擇縣市' }]}
                    validateStatus={
                      form.getFieldError('city').length ? 'error' : ''
                    }
                    style={{ minWidth: 120 }}
                  >
                    <Select
                      placeholder='縣市'
                      onChange={handleCityChange}
                      value={selectedCity}
                      style={{ width: 100 }}
                    >
                      {cityData.counties.map((city) => (
                        <Option key={city} value={city}>
                          {city}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item
                    name='district'
                    rules={[{ required: true, message: '請選擇鄉鎮市區' }]}
                    validateStatus={
                      form.getFieldError('district').length ? 'error' : ''
                    }
                    style={{ minWidth: 120 }}
                  >
                    <Select
                      placeholder='鄉鎮市區'
                      disabled={!selectedCity}
                      style={{ width: 120 }}
                    >
                      {districtOptions.map((d) => (
                        <Option key={d} value={d}>
                          {d}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item
                    name='road'
                    rules={[
                      { required: true, message: '請輸入路、街、村、段' }
                    ]}
                    validateStatus={
                      form.getFieldError('road').length ? 'error' : ''
                    }
                    style={{ minWidth: 160 }}
                  >
                    <Input
                      placeholder='路、街、村、段'
                      style={{ width: 140 }}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item name='neighborhood' style={{ width: 50 }}>
                    <Input placeholder='' style={{ width: 40 }} />
                  </Form.Item>
                </Col>
                <Col style={{ lineHeight: '32px' }}>鄰</Col>
                <Col>
                  <Form.Item name='lane' style={{ width: 50 }}>
                    <Input placeholder='' style={{ width: 40 }} />
                  </Form.Item>
                </Col>
                <Col style={{ lineHeight: '32px' }}>巷</Col>
                <Col>
                  <Form.Item name='alley' style={{ width: 50 }}>
                    <Input placeholder='' style={{ width: 40 }} />
                  </Form.Item>
                </Col>
                <Col style={{ lineHeight: '32px' }}>弄</Col>
                <Col>
                  <Form.Item name='street' style={{ width: 50 }}>
                    <Input placeholder='' style={{ width: 40 }} />
                  </Form.Item>
                </Col>
                <Col style={{ lineHeight: '32px' }}>街</Col>
                <Col>
                  <Form.Item
                    name='number'
                    rules={[{ required: true, message: '請輸入號之' }]}
                    validateStatus={
                      form.getFieldError('number').length ? 'error' : ''
                    }
                    style={{ width: 60 }}
                  >
                    <Input placeholder='' style={{ width: 50 }} />
                  </Form.Item>
                </Col>
                <Col style={{ lineHeight: '32px' }}>號之</Col>
                <Col>
                  <Form.Item name='floor' style={{ width: 60 }}>
                    <Input placeholder='' style={{ width: 50 }} />
                  </Form.Item>
                </Col>
                <Col style={{ lineHeight: '32px' }}>樓之</Col>
                <Col>
                  <Form.Item name='room' style={{ width: 60 }}>
                    <Input placeholder='' style={{ width: 50 }} />
                  </Form.Item>
                </Col>
                <Col style={{ lineHeight: '32px' }}>室</Col>
              </Row>
            </FormSection>

            <FormSection>
              <FormSectionTitle>
                <IdcardOutlined />
                會員身份證件資料
              </FormSectionTitle>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name='idNumber'
                    label='身分證字號'
                    rules={[
                      { required: true, message: '請輸入身分證字號' },
                      {
                        pattern: /^[A-Z][12]\d{8}$/,
                        message: '請輸入正確的身分證字號格式'
                      }
                    ]}
                  >
                    <Input placeholder='請輸入身分證字號' />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name='idCardIssueDate'
                    label='身分證發證日期'
                    rules={[
                      { required: true, message: '請選擇身分證發證日期' }
                    ]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name='idCardIssuePlace'
                    label='身分證發證地點'
                    rules={[
                      { required: true, message: '請選擇身分證發證地點' }
                    ]}
                  >
                    <Select placeholder='請選擇發證地點'>
                      <Option value='initial'>初發</Option>
                      <Option value='renewal'>換發</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name='idCardFront'
                    label='身分證正面圖片'
                    valuePropName='fileList'
                    getValueFromEvent={normFile}
                    rules={[
                      { required: true, message: '請上傳身分證正面圖片' }
                    ]}
                    extra='請上傳清晰的身分證正面照片'
                  >
                    <Upload
                      listType='picture'
                      maxCount={1}
                      beforeUpload={() => false}
                    >
                      <Button icon={<UploadOutlined />}>上傳正面</Button>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name='idCardBack'
                    label='身分證反面圖片'
                    valuePropName='fileList'
                    getValueFromEvent={normFile}
                    rules={[
                      { required: true, message: '請上傳身分證反面圖片' }
                    ]}
                    extra='請上傳清晰的身分證反面照片'
                  >
                    <Upload
                      listType='picture'
                      maxCount={1}
                      beforeUpload={() => false}
                    >
                      <Button icon={<UploadOutlined />}>上傳反面</Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name='healthCardImage'
                    label='健保卡圖片'
                    valuePropName='fileList'
                    getValueFromEvent={normFile}
                    rules={[{ required: true, message: '請上傳健保卡圖片' }]}
                    extra='請上傳清晰的健保卡照片'
                  >
                    <Upload
                      listType='picture'
                      maxCount={1}
                      beforeUpload={() => false}
                    >
                      <Button icon={<UploadOutlined />}>上傳健保卡圖片</Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
            </FormSection>

            <FormSection>
              <FormSectionTitle>
                <BankOutlined />
                金融機構賬戶資料
              </FormSectionTitle>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name='bankCategory'
                    label='金融機構分類'
                    rules={[{ required: true, message: '請選擇金融機構分類' }]}
                  >
                    <Select
                      placeholder='請選擇金融機構分類'
                      onChange={handleBankCategoryChange}
                    >
                      <Option value='bank'>銀行</Option>
                      <Option value='post'>郵局</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name='bankName'
                    label='銀行或郵局名稱'
                    rules={[
                      { required: true, message: '請選擇銀行或郵局名稱' }
                    ]}
                  >
                    <Select
                      placeholder='請選擇銀行或郵局名稱'
                      onChange={handleBankNameChange}
                      disabled={!selectedBankCategory}
                    >
                      {selectedBankCategory === 'bank'
                        ? getBankOptions().map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))
                        : getPostOptions().map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name='branchName'
                    label='分行或支局名稱'
                    rules={[
                      { required: true, message: '請輸入分行或支局名稱' }
                    ]}
                  >
                    <Input placeholder='請輸入分行或支局名稱' />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name='accountName'
                    label='戶名'
                    rules={[{ required: true, message: '請輸入戶名' }]}
                  >
                    <Input placeholder='請輸入戶名' />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name='accountNumber'
                    label='帳號'
                    rules={[{ required: true, message: '請輸入帳號' }]}
                  >
                    <Input placeholder='請輸入11-14碼帳號' maxLength={14} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name='bankbookImage'
                    label='存摺影本'
                    valuePropName='fileList'
                    getValueFromEvent={normFile}
                    rules={[{ required: true, message: '請上傳存摺影本' }]}
                  >
                    <Upload
                      listType='picture'
                      maxCount={1}
                      beforeUpload={() => false}
                    >
                      <Button icon={<UploadOutlined />}>上傳存摺影本</Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
            </FormSection>

            <div style={{ textAlign: 'center', marginTop: 40 }}>
              <Button
                type='primary'
                size='large'
                onClick={handleSubmit}
                loading={submitApplicationLoading}
                icon={<ArrowRightOutlined />}
                style={{
                  minWidth: 200,
                  height: 48,
                  borderRadius: 12,
                  fontWeight: 600
                }}
              >
                提交申請
              </Button>
            </div>
          </Form>
        </StyledCard>
        </div>
      </Spin>
    )
  }

  const handleHelpSubmit = () => {
    if (!helpMessage.trim()) {
      message.warning('請輸入您的問題')
      return
    }

    api
      .post(`/api/business_B/${businessId}/payment-help`, {
        message: helpMessage
      })
      .then(() => {
        message.success('已發送您的問題，客服會盡快回覆')
        setShowHelpModal(false)
        setHelpMessage('')
      })
      .catch(() => {
        message.error('發送失敗，請稍後重試')
      })
  }

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e
    }
    return e?.fileList
  }

  const handleBankCategoryChange = (value) => {
    setSelectedBankCategory(value)
    form.setFieldsValue({ bankName: undefined, branchName: undefined })
  }

  const handleBankNameChange = (value) => {
    form.setFieldsValue({ branchName: undefined })
  }

  const getBankOptions = () => {
    return bankPost
      .filter((item) => item.Key.endsWith('銀行'))
      .map((item) => ({
        value: item.Key,
        label: item.Key
      }))
  }

  const getPostOptions = () => {
    return bankPost
      .filter((item) => !item.Key.endsWith('銀行'))
      .map((item) => ({
        value: item.Key,
        label: item.Key
      }))
  }

  const handleCityChange = (value) => {
    setSelectedCity(value)
    form.setFieldsValue({ district: undefined })
    const cityObj = cityData.districts.find((c) => c.name === value)
    setDistrictOptions(cityObj ? cityObj.areas : [])
  }

  // ── 信用卡操作：取消授權、退款、查詢 ──────────────────────
  const [actionLoading, setActionLoading] = useState({})

  const handleCancelAuth = async (record) => {
    setActionLoading((prev) => ({ ...prev, [record.order_sn]: 'cancel' }))
    try {
      const response = await api.post('/api/payments/credit-card/cancel', {
        order_sn: record.order_sn,
        trade_no: record.trade_no,
        amount: record.amount,
        business_id: businessId
      })
      if (response.data.success) {
        message.success(response.data.message || '取消授權成功')
        fetchPaymentRecords()
      } else {
        message.error(response.data.message || '取消授權失敗')
      }
    } catch (error) {
      console.error('取消授權失敗:', error)
      message.error(getApiErrorMessage(error, '取消授權失敗'))
    } finally {
      setActionLoading((prev) => ({ ...prev, [record.order_sn]: null }))
    }
  }

  const handleRefund = async (record) => {
    if (!record.trade_no) {
      message.error('缺少藍新交易序號，無法退款')
      return
    }
    setActionLoading((prev) => ({ ...prev, [record.order_sn]: 'refund' }))
    try {
      const response = await api.post('/api/payments/credit-card/close', {
        order_sn: record.order_sn,
        trade_no: record.trade_no,
        amount: record.amount,
        close_type: 2, // 退款
        business_id: businessId
      })
      if (response.data.success) {
        message.success(response.data.message || '退款成功')
        fetchPaymentRecords()
      } else {
        message.error(response.data.message || '退款失敗')
      }
    } catch (error) {
      console.error('退款失敗:', error)
      message.error(getApiErrorMessage(error, '退款失敗'))
    } finally {
      setActionLoading((prev) => ({ ...prev, [record.order_sn]: null }))
    }
  }

  const handleQueryTrade = async (record) => {
    setActionLoading((prev) => ({ ...prev, [record.order_sn]: 'query' }))
    try {
      const response = await api.post('/api/payments/credit-card/query', {
        order_sn: record.order_sn,
        trade_no: record.trade_no,
        amount: record.amount,
        business_id: businessId
      })
      if (response.data.success) {
        const data = response.data.data
        Modal.info({
          title: '交易查詢結果',
          width: 520,
          content: (
            <div style={{ marginTop: 12 }}>
              <p><b>交易狀態：</b>{data.TradeStatus === '1' ? '已付款' : data.TradeStatus === '0' ? '未付款' : data.TradeStatus}</p>
              <p><b>交易序號：</b>{data.TradeNo || '-'}</p>
              <p><b>金額：</b>NT$ {data.Amt || record.amount}</p>
              <p><b>付款方式：</b>{data.PaymentType || '-'}</p>
              <p><b>付款時間：</b>{data.PayTime || '-'}</p>
              <p><b>授權碼：</b>{data.Auth || '-'}</p>
              <p><b>請款狀態：</b>{data.CloseStatus === '0' ? '未請款' : data.CloseStatus === '3' ? '已請款' : data.CloseStatus || '-'}</p>
              <p><b>退款狀態：</b>{data.BackStatus === '0' ? '未退款' : data.BackStatus === '3' ? '已退款' : data.BackStatus || '-'}</p>
            </div>
          )
        })
      } else {
        message.error(response.data.message || '查詢失敗')
      }
    } catch (error) {
      console.error('查詢交易失敗:', error)
      message.error(getApiErrorMessage(error, '查詢交易失敗'))
    } finally {
      setActionLoading((prev) => ({ ...prev, [record.order_sn]: null }))
    }
  }

  const renderPaymentRecords = () => {
    return (
      <Modal
        title='支付記錄'
        open={showPaymentRecords}
        onCancel={() => setShowPaymentRecords(false)}
        width={960}
        footer={null}
      >
        <Table
          dataSource={paymentRecords}
          columns={[
            {
              title: '訂單編號',
              dataIndex: 'order_sn',
              key: 'order_sn',
              ellipsis: true,
              width: 160
            },
            {
              title: '金額',
              dataIndex: 'amount',
              key: 'amount',
              width: 100,
              render: (amount) => `NT$ ${amount.toLocaleString()}`
            },
            {
              title: '支付方式',
              dataIndex: 'payment_type',
              key: 'payment_type',
              width: 80,
              render: (type) => (type === 'credit' ? '信用卡' : '其他')
            },
            {
              title: '狀態',
              dataIndex: 'status',
              key: 'status',
              width: 80,
              render: (status) => {
                const statusMap = {
                  completed: { text: '已完成', color: 'success' },
                  pending: { text: '處理中', color: 'warning' },
                  failed: { text: '失敗', color: 'error' },
                  refunded: { text: '已退款', color: 'default' }
                }
                const { text, color } = statusMap[status] || {
                  text: '未知',
                  color: 'default'
                }
                return <Tag color={color}>{text}</Tag>
              }
            },
            {
              title: '支付時間',
              dataIndex: 'pay_time',
              key: 'pay_time',
              width: 150,
              render: (time) =>
                time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-'
            },
            {
              title: '操作',
              key: 'actions',
              width: 200,
              render: (_, record) => {
                if (record.payment_type !== 'credit') return '-'
                const loading = actionLoading[record.order_sn]

                const menuItems = [
                  {
                    key: 'query',
                    icon: <SearchOutlined />,
                    label: '查詢交易狀態',
                    onClick: () => handleQueryTrade(record)
                  }
                ]

                // 只有已完成的交易才能取消授權或退款
                if (record.status === 'completed') {
                  menuItems.push(
                    {
                      key: 'cancel',
                      icon: <StopOutlined />,
                      label: '取消授權',
                      danger: true,
                      onClick: () => {
                        Modal.confirm({
                          title: '確認取消授權',
                          icon: <ExclamationCircleOutlined />,
                          content: `確定要取消此筆交易的授權嗎？\n訂單：${record.order_sn}\n金額：NT$ ${record.amount.toLocaleString()}\n\n取消授權後款項將不會從客戶帳戶扣除。`,
                          okText: '確認取消',
                          cancelText: '返回',
                          okType: 'danger',
                          onOk: () => handleCancelAuth(record)
                        })
                      }
                    },
                    {
                      key: 'refund',
                      icon: <RollbackOutlined />,
                      label: '退款',
                      danger: true,
                      onClick: () => {
                        Modal.confirm({
                          title: '確認退款',
                          icon: <ExclamationCircleOutlined />,
                          content: `確定要對此筆交易進行退款嗎？\n訂單：${record.order_sn}\n金額：NT$ ${record.amount.toLocaleString()}\n\n退款後款項將退回至客戶信用卡。`,
                          okText: '確認退款',
                          cancelText: '返回',
                          okType: 'danger',
                          onOk: () => handleRefund(record)
                        })
                      }
                    }
                  )
                }

                return (
                  <Space size='small'>
                    <Dropdown
                      menu={{ items: menuItems }}
                      trigger={['click']}
                    >
                      <Button
                        size='small'
                        loading={!!loading}
                      >
                        操作 ▾
                      </Button>
                    </Dropdown>
                  </Space>
                )
              }
            }
          ]}
          rowKey='order_sn'
          scroll={{ x: 800 }}
          size='small'
        />
      </Modal>
    )
  }

  const renderSettingsLoadingState = () => (
    <SettingsLoadingShell>
      <SettingsLoadingHero aria-hidden />
      <SettingsSpinWrap>
        <Spin size='large' tip='載入支付設定中…' />
      </SettingsSpinWrap>
      <Row gutter={[24, 24]}>
        {[0, 1, 2].map((k) => (
          <Col xs={24} sm={24} md={8} key={k}>
            <Card
              size='small'
              style={{ borderRadius: 12 }}
              styles={{ body: { padding: 20 } }}
            >
              <Skeleton active paragraph={{ rows: 2 }} title={{ width: '60%' }} />
            </Card>
          </Col>
        ))}
      </Row>
      <StyledCard style={{ marginTop: 24 }}>
        <Skeleton active paragraph={{ rows: 10 }} title={{ width: '38%' }} />
      </StyledCard>
    </SettingsLoadingShell>
  )

  return (
    <StyledLayout>
      {settingsLoading ? (
        renderSettingsLoadingState()
      ) : (
        <ContentReveal>
          {hasApplied ? renderStatusContent() : renderApplicationForm()}
          {/* hasApplied 時仍掛載一個隱藏 Form 以避免 useForm 警告 */}
          {hasApplied && <Form form={form} component={false} />}
        </ContentReveal>
      )}
      {renderPaymentRecords()}

      <Modal
        title='聯繫客服'
        open={showHelpModal}
        onOk={handleHelpSubmit}
        onCancel={() => {
          setShowHelpModal(false)
          setHelpMessage('')
        }}
        okText='發送'
        cancelText='取消'
      >
        <Form layout='vertical'>
          <Form.Item label='請描述您遇到的問題' required>
            <TextArea
              rows={4}
              value={helpMessage}
              onChange={(e) => setHelpMessage(e.target.value)}
              placeholder='請詳細描述您在申請過程中遇到的問題，客服會盡快協助您解決。'
            />
          </Form.Item>
        </Form>
      </Modal>
    </StyledLayout>
  )
}

export default PaymentSettings
